import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SatelliteLockService } from 'src/app/modules/satellite-lock/satellite-lock.service';
import { Permission } from 'src/app/core/resources/permission';
import { FilterModel } from 'src/app/core/models/filter';
import { Utils } from 'src/app/core/resources/utils';
import { NgxSpinnerService } from 'ngx-spinner';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { SatelliteLock } from 'src/app/core/interfaces/satelliteLock';

interface SatelliteLockListResponse {
  items: SatelliteLock[];
  nextPageKey?: string;
  totalItems?: number;
}


@Component({
  selector: 'app-list-satellite-lock',
  templateUrl: './list-satellite-lock.component.html',
  styleUrls: ['./list-satellite-lock.component.scss']
})
export class ListSatelliteLockComponent implements OnInit {
  permission = Permission;
  optionsFilter: FilterModel[] = [];
  filters: string = '';
  private isLoadingNextPage: boolean = false;
  private nextPageKey: string | null = null;
  private totalItems: number = 0;
  private loadedItems: number = 0;
  private lastUsedPageKey: string | null = null;
  private usedPageKeys: Set<string> = new Set();

  constructor(
    public service: SatelliteLockService,
    private router: Router,
    public utils: Utils,
    private spinner: NgxSpinnerService,
    private snackBarService: SnackBarService
  ) { }


  ngOnInit(): void {
    this.service.paginationList.setPageSize(10);
    this.optionsFilter = this.service.getOptionsFilter();
  }


  public getSatelliteLocks(filters: string = this.filters, concat: boolean = false): void {
    if (concat && this.isLoadingNextPage) return;
    if (!concat) {
      this.resetPagination();
      this.service.paginationList.setEnablePagingScroll(false);
      this.isLoadingNextPage = false;
    } else {
      this.isLoadingNextPage = true;
    }

    this.filters = filters;
    this.spinner.show();
    const nextPageKey = concat ? this.nextPageKey : null;

    this.service.getSatelliteLocks(
      this.service.paginationList.getPageSize(),
      nextPageKey,
      filters
    ).subscribe(
      (response: SatelliteLockListResponse) => {
        this.processPaginationResponse(response, nextPageKey, concat);
        this.spinner.hide();
      },
      (error) => {
        this.handlePaginationError(error);
      }
    );
  }


  private processPaginationResponse(
    response: SatelliteLockListResponse,
    nextPageKey: string | null,
    concat: boolean
  ): void {
    const satelliteLocks = response.items || [];
    const pageSize = this.service.paginationList.getPageSize();
    const itemsReceived = satelliteLocks.length;
    const isDuplicatePageKey = nextPageKey && (
      nextPageKey === this.lastUsedPageKey ||
      this.usedPageKeys.has(nextPageKey)
    );
    if (nextPageKey) {
      this.usedPageKeys.add(nextPageKey);
      this.loadedItems += itemsReceived;
    } else this.loadedItems = itemsReceived;
    this.totalItems = response.totalItems || 0;
    this.lastUsedPageKey = nextPageKey;
    const hasReachedTotal = this.totalItems > 0 && this.loadedItems >= this.totalItems;
    const noMorePages = !response.nextPageKey || response.nextPageKey.trim() === '';
    const receivedLessThanRequested = itemsReceived < pageSize;
    if (isDuplicatePageKey || hasReachedTotal || noMorePages || receivedLessThanRequested) this.nextPageKey = null;
    else this.nextPageKey = response.nextPageKey;
    if (!this.utils.isDefined(satelliteLocks) || satelliteLocks.length < 1) return this.service.paginationList.setEnablePagingScroll(false);
    if (this.service.paginationList.getList().length && concat) {
      this.service.paginationList.setList(
        this.service.paginationList.getList().concat(satelliteLocks)
      );
    } else this.service.paginationList.setList(satelliteLocks);
    const hasMorePages = this.hasMorePages();
    this.service.paginationList.setEnablePagingScroll(hasMorePages);
    this.isLoadingNextPage = false;
  }


  private handlePaginationError(error: any): void {
    this.resetPagination();
    this.service.paginationList.setEnablePagingScroll(false);
    this.isLoadingNextPage = false;
    this.spinner.hide();
    this.snackBarService.openSnackBar(
      'Error al cargar la lista de candados satelitales',
      undefined,
      'error'
    );
    console.error('Error loading satellite locks:', error);
  }


  private resetPagination(): void {
    this.nextPageKey = null;
    this.totalItems = 0;
    this.loadedItems = 0;
    this.lastUsedPageKey = null;
    this.usedPageKeys.clear();
    this.service.paginationList.setList([]);
  }


  private hasMorePages(): boolean {
    return this.nextPageKey !== null &&
      this.nextPageKey !== undefined &&
      this.nextPageKey.trim() !== '';
  }

  public getPaginationDebugInfo(): {
    nextPageKey: string | null,
    totalItems: number,
    loadedItems: number,
    lastUsedPageKey: string | null,
    hasMorePages: boolean
  } {
    return {
      nextPageKey: this.nextPageKey,
      totalItems: this.totalItems,
      loadedItems: this.loadedItems,
      lastUsedPageKey: this.lastUsedPageKey,
      hasMorePages: this.hasMorePages()
    };
  }

  onScrollDown(): void {
    const canPaginate = this.service.paginationList.getEnablePagingScroll();
    const hasMorePages = this.hasMorePages();
    if (canPaginate && hasMorePages) {
      this.getSatelliteLocks(this.filters, true);
    }
  }

  goToCreate(): void {
    this.router.navigate(['administration/satellite-lock/create']);
  }

}
